// Purpose: admin break-glass forensics grant + session listing + termination.

import { api, csrfHeaders } from './client.ts';

export interface ForensicsGrantResult {
  accessId: string;
  grantToken: string;
  expiresAt: string;
}

export interface ActiveGrantSummary {
  accessId: string;
  adminId: string;
  adminEmail: string;
  targetType: string;
  targetId: string;
  grantedAt: string;
  expiresAt: string;
}

export async function grantForensicsAccess(input: {
  targetType: 'share' | 'dropbox_upload';
  targetId: string;
  justification: string;
}): Promise<ForensicsGrantResult> {
  const res = await api.post<ForensicsGrantResult>(
    '/admin/forensics/grant',
    { ...input, acknowledged: true },
    { headers: csrfHeaders() }
  );
  return res.data;
}

export async function closeForensicsAccess(
  accessId: string,
  bytesViewed: number,
  durationSeconds: number
): Promise<void> {
  await api.post(
    `/admin/forensics/grants/${accessId}/close`,
    { bytesViewed, durationSeconds },
    { headers: csrfHeaders() }
  );
}

export async function listActiveGrants(): Promise<ActiveGrantSummary[]> {
  const res = await api.get<{ sessions: ActiveGrantSummary[] }>('/admin/forensics/sessions');
  return res.data.sessions;
}

export async function terminateGrant(accessId: string, reason: string): Promise<void> {
  await api.post(
    `/admin/forensics/sessions/${accessId}/terminate`,
    { reason },
    { headers: csrfHeaders() }
  );
}

// Commit 8b.12: type-dispatched break-glass target lookup. Powers the
// SearchableSelect in admin/Forensics.tsx so an operator picks by
// human-readable label instead of pasting a UUID. Server returns
// decrypted labels.
export interface BreakGlassTargetItem {
  id: string;
  label: string;
  createdAt: string;
  isActive: boolean;
}

const TARGET_PATHS: Record<'share' | 'dropbox_upload', string> = {
  share: '/admin/break-glass/targets/shares',
  dropbox_upload: '/admin/break-glass/targets/dropbox-uploads',
};

export async function listBreakGlassTargets(
  targetType: 'share' | 'dropbox_upload'
): Promise<BreakGlassTargetItem[]> {
  const path = TARGET_PATHS[targetType];
  const res = await api.get<{ items: BreakGlassTargetItem[] }>(path);
  return res.data.items;
}
